/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationUserSessionIssuerRequest,
  GetOrganizationUserSessionIssuerSecurity,
} from "../models/operations/getorganizationusersessionissuer.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuerQuery,
  OrganizationUserSessionIssuerQueryData,
  prefetchOrganizationUserSessionIssuer,
  queryKeyOrganizationUserSessionIssuer,
} from "./organizationUserSessionIssuer.core.js";
export {
  buildOrganizationUserSessionIssuerQuery,
  type OrganizationUserSessionIssuerQueryData,
  prefetchOrganizationUserSessionIssuer,
  queryKeyOrganizationUserSessionIssuer,
};

export type OrganizationUserSessionIssuerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getIssuer organizationUserSessionIssuers
 *
 * @remarks
 * Get any user_session_issuer in the caller's organization, organization-owned or project-specific, by id. Requires org:read.
 */
export function useOrganizationUserSessionIssuer(
  request: GetOrganizationUserSessionIssuerRequest,
  security?: GetOrganizationUserSessionIssuerSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuerQueryData,
    OrganizationUserSessionIssuerQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuerQueryData,
  OrganizationUserSessionIssuerQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getIssuer organizationUserSessionIssuers
 *
 * @remarks
 * Get any user_session_issuer in the caller's organization, organization-owned or project-specific, by id. Requires org:read.
 */
export function useOrganizationUserSessionIssuerSuspense(
  request: GetOrganizationUserSessionIssuerRequest,
  security?: GetOrganizationUserSessionIssuerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuerQueryData,
    OrganizationUserSessionIssuerQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuerQueryData,
  OrganizationUserSessionIssuerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationUserSessionIssuerData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: OrganizationUserSessionIssuerQueryData,
): OrganizationUserSessionIssuerQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuer(...queryKeyBase);

  return client.setQueryData<OrganizationUserSessionIssuerQueryData>(key, data);
}

export function invalidateOrganizationUserSessionIssuer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "get",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationUserSessionIssuers", "get"],
  });
}
